import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShoppingCart, X, Star, Sparkles, Crown } from 'lucide-react';

const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: 'free_trial_used' | 'no_games';
  gamesRemaining?: number;
}

// الأسعار بالريال السعودي
const PACKAGES = [
  {
    id: 'basic',
    name: 'لعبة واحدة',
    games: 1,
    price: 8,
    currency: 'ر.س',
    popular: false,
  },
  {
    id: 'popular',
    name: 'ثلاث ألعاب',
    games: 3,
    price: 20,
    currency: 'ر.س',
    popular: true,
    discount: 17,
  },
  {
    id: 'premium',
    name: 'سبع ألعاب',
    games: 7,
    price: 35,
    currency: 'ر.س',
    popular: false,
    discount: 38,
  },
];

export const PurchaseModal = ({ isOpen, onClose, reason = 'free_trial_used', gamesRemaining = 0 }: PurchaseModalProps) => {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  if (!isOpen) return null;

  const getTitle = () => {
    switch (reason) {
      case 'free_trial_used':
        return 'انتهت تجربتك المجانية';
      case 'no_games':
        return 'لا توجد ألعاب متبقية';
      default:
        return 'شراء ألعاب';
    }
  };

  const getMessage = () => {
    switch (reason) {
      case 'free_trial_used':
        return 'جرّبت اللعبة مجاناً؟ اختر باقة لمواصلة اللعب مع من تحب.';
      case 'no_games':
        return `باقي لديك ${gamesRemaining} لعبة. اشترِ المزيد لمواصلة المتعة.`;
      default:
        return 'اختر عدد الألعاب المناسب لك';
    }
  };

  const handlePurchase = () => {
    // Navigate to payment page (to be implemented)
    // For now, just close the modal
    alert('سيتم تحويلك لصفحة الدفع قريباً!');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={arabicFont}>
      <div className="bg-white rounded-3xl max-w-3xl w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 left-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-8 h-8" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2" style={ruqaaFont}>
              {getTitle()}
            </h2>
            <p className="text-green-100">{getMessage()}</p>
          </div>
        </div>

        {/* Packages */}
        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-4">
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`relative rounded-2xl p-4 cursor-pointer transition-all border-2 ${
                  selectedPackage === pkg.id
                    ? 'border-green-500 bg-green-50 scale-105'
                    : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                } ${pkg.popular ? 'ring-2 ring-yellow-400 ring-offset-2' : ''}`}
              >
                {/* Popular Badge */}
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    الأفضل قيمة
                  </div>
                )}

                {/* Discount Badge */}
                {pkg.discount && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    -{pkg.discount}%
                  </div>
                )}

                <div className="text-center">
                  <h3 className="font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-green-600 mb-1">
                    {pkg.price} {(pkg as { currency?: string }).currency || 'ر.س'}
                  </div>
                  <p className="text-gray-500 text-sm mb-3">{pkg.games} {pkg.games === 1 ? 'لعبة' : 'ألعاب'}</p>
                  
                  <div className={`w-full py-2 rounded-xl text-sm font-bold ${
                    selectedPackage === pkg.id
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedPackage === pkg.id ? '✓ تم الاختيار' : 'اختر'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handlePurchase}
              disabled={!selectedPackage}
              className={`flex-1 py-4 text-lg font-bold rounded-xl transition-all ${
                selectedPackage
                  ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Sparkles className="w-5 h-5 ml-2" />
              {selectedPackage ? 'اشترِ الآن' : 'اختر باقة أولاً'}
            </Button>
            
            <Button
              onClick={onClose}
              variant="outline"
              className="sm:w-auto py-4 text-lg font-bold rounded-xl border-2"
            >
              لاحقاً
            </Button>
          </div>

          {/* Benefits */}
          <div className="mt-6 bg-gray-50 rounded-xl p-4">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              مميزات الشراء
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs">✓</span>
                الوصول لجميع الفئات والأقسام
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs">✓</span>
                أسئلة جديدة ومتنوعة
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs">✓</span>
                اللعب مع العائلة والأصدقاء
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs">✓</span>
                دعم فني على مدار الساعة
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
